import { memo } from 'react'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'

// Components
import { LicenseCategoryIcon, Tooltip, IconSyncCircle, IconPublicDomainCircle, Link } from '@tracklib/kit'

// Selectors
import { selectAll, genreEntities, trackTypeEntities, ownProfileEntity, authDomain } from 'selectors'

// Types
import { Song, Genre, Profile, EntityState, TrackType, Track } from 'types'

// Utils
import { LicensePrice } from 'components/base/price'
import { KEYS_CHOICES } from '@constants'
import formatDate from 'lib/formatdate'

import styles from './footer.css'

type To = {
  pathname: string
  query?: {
    [key: string]: string | number
  }
}
type LinkItem = { label?: string; links: Array<{ label: string; to: To }> }
type FooterItemProps = {
  label?: string
  links: Array<{
    to: To
    label: string
  }>
  showDividerLine: boolean
  strippedMode?: boolean
}

export const FooterItem = memo<FooterItemProps>(
  ({ label, links, showDividerLine, strippedMode }: FooterItemProps): JSX.Element => (
    <span>
      {label && <span className={styles.label}>{label}</span>}
      {links.map((link, i) => {
        if (!link) {
          return null
        }
        const { pathname, query } = link.to
        return (
          <span key={String(i)}>
            {strippedMode ? (
              link.label
            ) : (
              <Link to={pathname} query={query}>
                {link.label}
              </Link>
            )}
            {i !== links.length - 1 && ', '}
          </span>
        )
      })}
      {showDividerLine && <span className={styles.greySep}>|</span>}
    </span>
  ),
)

type SongItemFooterProps = {
  song: Song
  track: Track
  renderSpecificTrackNames?: boolean
  isRelated?: boolean
  strippedMode?: boolean
  // mapStateToProps
  trackTypes: EntityState<TrackType>
  profile: Profile
  genres: EntityState<Genre>
  adminModeActive: boolean
}

const SongItemFooter = ({
  song,
  track,
  renderSpecificTrackNames,
  profile,
  trackTypes,
  genres,
  isRelated,
  strippedMode,
  adminModeActive,
}: SongItemFooterProps): JSX.Element => {
  const isNotAvailableForLicensing = ![2, 3].includes(song.state)
  const priceCategory = song.licenseinfo.license_price_category
  const { BPM } = track
  let bpmArray = [BPM]
  if (BPM > 120) {
    bpmArray = [BPM / 2, BPM]
  } else if (BPM < 80) {
    bpmArray = [BPM, BPM * 2]
  }
  const keyId = song.tonal
  const keyObj = keyId && KEYS_CHOICES.find(x => x.id === keyId)

  const releaseYear = song.release_year
  const nrOfGenres = song.genres.length
  const genreLinks: LinkItem | null =
    nrOfGenres > 0
      ? {
          links: song.genres.map((genre: number | Genre) => {
            const genreObj = genres[String(genre)]
            return {
              label: genreObj.name,
              to: { pathname: `/genre/${genreObj.slug}/` },
            }
          }),
        }
      : null

  const showAdminMode = profile?.is_staff && adminModeActive
  const items: Array<LinkItem> = [
    {
      label: 'Released: ',
      links: [
        {
          label: String(releaseYear),
          to: {
            pathname: '/tracks',
            query: {
              release_year_after: releaseYear,
              release_year_before: releaseYear,
            },
          },
        },
      ],
    },
    ...(showAdminMode
      ? [
          {
            label: 'TrackID: ',
            links: [{ label: String(track.id), to: { pathname: '/tracks' } }],
          },
        ]
      : []),
    ...(showAdminMode
      ? [
          {
            label: 'SongId: ',
            links: [
              {
                label: String(track.song),
                to: { pathname: '/tracks' },
              },
            ],
          },
        ]
      : []),
    ...(BPM
      ? [
          {
            links: [
              {
                label: `${bpmArray.join(' / ')} BPM`,
                to: {
                  pathname: '/tracks',
                  query: { bpm_over: BPM, bpm_under: BPM },
                },
              },
            ],
          },
        ]
      : []),
    ...(keyObj && keyObj.name
      ? [
          {
            links: [
              {
                label: keyObj.name,
                to: {
                  pathname: '/tracks',
                  query: { keys: keyObj.id },
                },
              },
            ],
          },
        ]
      : []),
    ...(genreLinks ? [genreLinks] : []),
    ...(renderSpecificTrackNames && !isRelated
      ? [
          {
            links: [
              {
                label: `${trackTypes[String(track.type)].name} track`,
                to: {
                  pathname: '/tracks',
                  query: { types: track.type },
                },
              },
            ],
          },
        ]
      : []),
    ...(showAdminMode
      ? [
          {
            label: 'Popularity: ',
            links: [
              {
                label: String(track.popularity),
                to: { pathname: '/tracks' },
              },
            ],
          },
        ]
      : []),
    ...(showAdminMode
      ? [
          {
            label: 'Published: ',
            links: [
              {
                label: formatDate(String(song.publish_date), 'MMM do y') || '',
                to: { pathname: '/tracks' },
              },
            ],
          },
        ]
      : []),
  ]

  return (
    <footer className={styles.footer} datatesting="song_item_footer">
      <div className={styles.linksWrapper}>
        <div className={styles.links} datatesting="song_footerlinks">
          {items.map(
            (item, index) =>
              item.links && (
                <FooterItem
                  label={item.label}
                  links={item.links}
                  showDividerLine={index !== items.length - 1}
                  key={String(index)}
                  strippedMode={strippedMode}
                />
              ),
          )}
        </div>
        <div className={styles.licensingInfoWrapper}>
          <div data-tooltip-wrapper>
            <Link to="/licensing/#revenueshare" className={styles.licensingInfo} datatesting="song_categoryfooter">
              <span>License</span>
              <LicenseCategoryIcon isNotAvailableForLicensing={isNotAvailableForLicensing} priceCategory={priceCategory} />
            </Link>
            <Tooltip>
              {isNotAvailableForLicensing ? (
                <span>
                  This track has been unpublished and <br /> is not available for licensing
                </span>
              ) : (
                <span>
                  License price: <LicensePrice category={priceCategory} />
                </span>
              )}
            </Tooltip>
          </div>
          {!song.licenseinfo.allow_sync && (
            <div data-tooltip-wrapper>
              <Link href="https://support.tracklib.com/hc/en-us/articles/360009945219" target="_blank" datatesting="song_categoryfooter">
                <IconSyncCircle className={styles.licenseIcon} />
              </Link>
              <Tooltip>
                <span>Sync on request</span>
              </Tooltip>
            </div>
          )}
          {(song.licenseinfo.public_domain || song.licenseinfo.spoken_word) && (
            <div data-tooltip-wrapper>
              <Link href="https://support.tracklib.com/hc/en-us/articles/360009859180" target="_blank" datatesting="song_categoryfooter">
                <IconPublicDomainCircle className={styles.licenseIcon} />
              </Link>
              <Tooltip>
                <span>No original publisher</span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </footer>
  )
}

const mapStateToProps = createSelector(
  [selectAll, genreEntities, trackTypeEntities, ownProfileEntity, authDomain],
  (state, genres, trackTypes, profile, auth) => ({
    genres,
    trackTypes,
    profile,
    adminModeActive: auth.adminModeActive,
  }),
)
export { SongItemFooter as Component }
export default connect(mapStateToProps)(SongItemFooter)
